import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const EffectiveTaxRateCalculator = () => {
  const content = article.find((item) => item.id === 8);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    // Income Inputs
    grossIncome: "100000",
    filingStatus: "single",
    state: "CA",
    
    // Investment Income
    qualifiedDividends: "5000",
    nonQualifiedDividends: "2000",
    shortTermGains: "3000",
    longTermGains: "7000",
    interestIncome: "1500",
    
    // Deductions & Credits
    standardDeduction: "12950",
    itemizedDeduction: "0",
    retirementContributions: "6000",
    hsaContributions: "0",
    educationCredits: "0",
    childTaxCredit: "0",
    
    // Tax Attributes
    amtPreference: "0",
    netInvestmentTax: "0",
    
    // Advanced Settings
    additionalMedicareTax: "0",
    stateLocalTaxes: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateTaxes();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  // Tax brackets for 2023 (simplified)
  const taxBrackets = {
    single: [
      { min: 0, max: 11000, rate: 0.10 },
      { min: 11001, max: 44725, rate: 0.12 },
      { min: 44726, max: 95375, rate: 0.22 },
      { min: 95376, max: 182100, rate: 0.24 },
      { min: 182101, max: 231250, rate: 0.32 },
      { min: 231251, max: 578125, rate: 0.35 },
      { min: 578126, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 22000, rate: 0.10 },
      { min: 22001, max: 89450, rate: 0.12 },
      { min: 89451, max: 190750, rate: 0.22 },
      { min: 190751, max: 364200, rate: 0.24 },
      { min: 364201, max: 462500, rate: 0.32 },
      { min: 462501, max: 693750, rate: 0.35 },
      { min: 693751, max: Infinity, rate: 0.37 }
    ]
  };

  // State tax rates (simplified examples)
  const stateTaxRates = {
    CA: { rate: 0.093, name: "California" },
    NY: { rate: 0.0882, name: "New York" },
    TX: { rate: 0, name: "Texas" },
    FL: { rate: 0, name: "Florida" },
    IL: { rate: 0.0495, name: "Illinois" }
  };

  const calculateTaxes = () => {
    const {
      grossIncome,
      filingStatus,
      state,
      qualifiedDividends,
      nonQualifiedDividends,
      shortTermGains,
      longTermGains,
      interestIncome,
      standardDeduction,
      itemizedDeduction,
      retirementContributions,
      hsaContributions,
      educationCredits,
      childTaxCredit,
      amtPreference,
      additionalMedicareTax
    } = inputs;

    // Parse inputs
    const gross = parseFloat(grossIncome) || 0;
    const qualDiv = parseFloat(qualifiedDividends) || 0;
    const nonQualDiv = parseFloat(nonQualifiedDividends) || 0;
    const stGains = parseFloat(shortTermGains) || 0;
    const ltGains = parseFloat(longTermGains) || 0;
    const interest = parseFloat(interestIncome) || 0;
    const stdDeduction = parseFloat(standardDeduction) || 0;
    const itemDeduction = parseFloat(itemizedDeduction) || 0;
    const retirement = parseFloat(retirementContributions) || 0;
    const hsa = parseFloat(hsaContributions) || 0;
    const education = parseFloat(educationCredits) || 0;
    const childCredit = parseFloat(childTaxCredit) || 0;

    // Calculate adjusted gross income
    const totalInvestmentIncome = qualDiv + nonQualDiv + stGains + ltGains + interest;
    const agi = gross + totalInvestmentIncome - retirement - hsa;

    // Calculate taxable income
    const deduction = Math.max(stdDeduction, itemDeduction);
    const taxableIncome = Math.max(0, agi - deduction);

    // Calculate federal income tax
    const brackets = taxBrackets[filingStatus] || taxBrackets.single;
    let federalTax = 0;
    let remainingIncome = taxableIncome;

    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;
      
      const bracketAmount = Math.min(remainingIncome, bracket.max - bracket.min + 1);
      federalTax += bracketAmount * bracket.rate;
      remainingIncome -= bracketAmount;
    }

    // Calculate capital gains tax
    const longTermGainsTax = Math.max(0, ltGains * 0.15); // Simplified
    const qualifiedDividendsTax = Math.max(0, qualDiv * 0.15); // Simplified

    // Calculate NIIT (Net Investment Income Tax)
    const niitThreshold = filingStatus === "single" ? 200000 : 250000;
    const niit = agi > niitThreshold ? Math.max(0, (agi - niitThreshold) * 0.038) : 0;

    // Calculate state tax
    const stateTaxInfo = stateTaxRates[state] || { rate: 0, name: "Other" };
    const stateTax = taxableIncome * stateTaxInfo.rate;

    // Calculate additional Medicare tax
    const medicareThreshold = filingStatus === "single" ? 200000 : 250000;
    const additionalMedicare = agi > medicareThreshold ? Math.max(0, (agi - medicareThreshold) * 0.009) : 0;

    // Total taxes
    const totalTax = federalTax + longTermGainsTax + qualifiedDividendsTax + niit + stateTax + additionalMedicare;
    
    // Apply credits
    const totalCredits = education + childCredit;
    const finalTax = Math.max(0, totalTax - totalCredits);

    // Effective tax rates
    const effectiveFederalRate = (federalTax / gross) * 100;
    const effectiveTotalRate = (finalTax / gross) * 100;
    const marginalRate = brackets.find(bracket => taxableIncome >= bracket.min && taxableIncome <= bracket.max)?.rate * 100 || 0;

    const resultData = {
      // Income Breakdown
      grossIncome: gross,
      agi: agi,
      taxableIncome: taxableIncome,
      
      // Tax Breakdown
      federalTax: federalTax,
      longTermGainsTax: longTermGainsTax,
      qualifiedDividendsTax: qualifiedDividendsTax,
      niit: niit,
      stateTax: stateTax,
      additionalMedicareTax: additionalMedicare,
      totalCredits: totalCredits,
      finalTax: finalTax,
      
      // Rates
      effectiveFederalRate: effectiveFederalRate,
      effectiveTotalRate: effectiveTotalRate,
      marginalRate: marginalRate,
      stateTaxRate: stateTaxInfo.rate * 100,
      
      // Investment Tax Details
      totalInvestmentIncome: totalInvestmentIncome,
      investmentTax: longTermGainsTax + qualifiedDividendsTax + niit,
      
      // After-tax values
      afterTaxIncome: gross - finalTax,
      taxSavingsFromDeductions: deduction * 0.24 // Simplified
    };

    setResult(resultData);
  };

  const calculateTaxProjection = () => {
    const { grossIncome, timePeriod, annualReturn } = inputs;
    
    if (!grossIncome) return;

    const initial = parseFloat(grossIncome);
    const years = parseInt(timePeriod) || 10;
    const annualGrowth = parseFloat(annualReturn || 5) / 100;

    const projectionData = [];
    let currentIncome = initial;

    for (let i = 0; i <= years; i++) {
      if (i > 0) {
        currentIncome = currentIncome * (1 + annualGrowth);
      }
      
      // Simplified tax calculation for projection
      const simpleTax = currentIncome * 0.25; // Assume 25% effective rate
      
      projectionData.push({
        year: i,
        income: currentIncome,
        estimatedTax: simpleTax,
        afterTaxIncome: currentIncome - simpleTax
      });
    }

    return projectionData;
  };

  const resetCalculator = () => {
    setInputs({
      grossIncome: "100000",
      filingStatus: "single",
      state: "CA",
      qualifiedDividends: "5000",
      nonQualifiedDividends: "2000",
      shortTermGains: "3000",
      longTermGains: "7000",
      interestIncome: "1500",
      standardDeduction: "12950",
      itemizedDeduction: "0",
      retirementContributions: "6000",
      hsaContributions: "0",
      educationCredits: "0",
      childTaxCredit: "0",
      amtPreference: "0",
      netInvestmentTax: "0",
      additionalMedicareTax: "0",
      stateLocalTaxes: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Tax Metric,Amount,Rate",
      `Gross Income,${result.grossIncome.toFixed(2)},`,
      `Adjusted Gross Income,${result.agi.toFixed(2)},`,
      `Taxable Income,${result.taxableIncome.toFixed(2)},`,
      `Federal Tax,${result.federalTax.toFixed(2)},`,
      `Long-term Gains Tax,${result.longTermGainsTax.toFixed(2)},`,
      `Qualified Dividends Tax,${result.qualifiedDividendsTax.toFixed(2)},`,
      `State Tax,${result.stateTax.toFixed(2)},`,
      `Total Tax,${result.finalTax.toFixed(2)},`,
      `Effective Federal Rate,,${result.effectiveFederalRate.toFixed(2)}%`,
      `Effective Total Rate,,${result.effectiveTotalRate.toFixed(2)}%`,
      `Marginal Rate,,${result.marginalRate.toFixed(2)}%`,
      `After-tax Income,${result.afterTaxIncome.toFixed(2)},`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tax-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Tax data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Tax calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Tax calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      investor: { 
        qualifiedDividends: "15000", 
        longTermGains: "25000",
        retirementContributions: "19500"
      },
      trader: {
        shortTermGains: "20000",
        longTermGains: "5000",
        retirementContributions: "6000"
      },
      retiree: {
        grossIncome: "80000",
        qualifiedDividends: "12000",
        longTermGains: "15000"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} tax preset applied`, "info");
  };

  // Chart Data
  const taxBreakdownChartData = result ? {
    labels: ["Federal Income Tax", "Capital Gains Tax", "State Tax", "NIIT/Medicare", "Tax Credits"],
    datasets: [{
      data: [
        result.federalTax,
        result.investmentTax,
        result.stateTax,
        result.niit + result.additionalMedicareTax,
        -result.totalCredits
      ],
      backgroundColor: ["#19692C", "#28A745", "#2B8C3A", "#1A7C2A", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const incomeBreakdownChartData = result ? {
    labels: ["Ordinary Income", "Qualified Dividends", "Long-term Gains", "Other Investment"],
    datasets: [{
      label: 'Income Sources',
      data: [
        result.grossIncome - result.totalInvestmentIncome,
        parseFloat(inputs.qualifiedDividends) || 0,
        parseFloat(inputs.longTermGains) || 0,
        (parseFloat(inputs.nonQualifiedDividends) || 0) + 
        (parseFloat(inputs.shortTermGains) || 0) + 
        (parseFloat(inputs.interestIncome) || 0)
      ],
      backgroundColor: ["#19692C", "#28A745", "#2B8C3A", "#1A7C2A"],
      borderWidth: 2
    }]
  } : null;

  const projectionData = calculateTaxProjection();
  const projectionChartData = projectionData ? {
    labels: projectionData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Gross Income',
        data: projectionData.map(d => d.income),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true
      },
      {
        label: 'After-tax Income',
        data: projectionData.map(d => d.afterTaxIncome),
        borderColor: "#28A745",
        backgroundColor: "rgba(40, 167, 69, 0.1)",
        fill: true
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Effective Tax Rate Calculator</h1>
        </div>
        <p>Calculate your effective tax rate, optimize investment taxes, and plan tax-efficient strategies</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Tax Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        {calc.result.effectiveTotalRate.toFixed(1)}% Eff. Rate
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Tax Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "projection" ? "active" : ""}`}
                onClick={() => setActiveTab("projection")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Projection' : 'Tax Projection'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-magic"></i>
                {isMobile ? 'Optimize' : 'Tax Optimization'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Tax Scenarios:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('investor')}>
                Investor
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('trader')}>
                Active Trader
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('retiree')}>
                Retiree
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-money-bill-wave"></i>
                    Income Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Gross Income</label>
                      <input 
                        type="number" 
                        name="grossIncome" 
                        value={inputs.grossIncome} 
                        onChange={handleChange}
                        placeholder="100000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="head">Head of Household</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-map-marker-alt"></i> State</label>
                      <select name="state" value={inputs.state} onChange={handleChange}>
                        <option value="CA">California</option>
                        <option value="NY">New York</option>
                        <option value="TX">Texas</option>
                        <option value="FL">Florida</option>
                        <option value="IL">Illinois</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Income
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Qualified Dividends</label>
                      <input 
                        type="number" 
                        name="qualifiedDividends" 
                        value={inputs.qualifiedDividends} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Non-Qualified Dividends</label>
                      <input 
                        type="number" 
                        name="nonQualifiedDividends" 
                        value={inputs.nonQualifiedDividends} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Short-term Gains</label>
                      <input 
                        type="number" 
                        name="shortTermGains" 
                        value={inputs.shortTermGains} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-seedling"></i> Long-term Gains</label>
                      <input 
                        type="number" 
                        name="longTermGains" 
                        value={inputs.longTermGains} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Interest Income</label>
                      <input 
                        type="number" 
                        name="interestIncome" 
                        value={inputs.interestIncome} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-hand-holding-usd"></i>
                    Deductions & Credits
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Standard Deduction</label>
                      <input 
                        type="number" 
                        name="standardDeduction" 
                        value={inputs.standardDeduction} 
                        onChange={handleChange}
                        placeholder="12950"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-receipt"></i> Itemized Deduction</label>
                      <input 
                        type="number" 
                        name="itemizedDeduction" 
                        value={inputs.itemizedDeduction} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-piggy-bank"></i> Retirement Contributions</label>
                      <input 
                        type="number" 
                        name="retirementContributions" 
                        value={inputs.retirementContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-heartbeat"></i> HSA Contributions</label>
                      <input 
                        type="number" 
                        name="hsaContributions" 
                        value={inputs.hsaContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-graduation-cap"></i> Education Credits</label>
                      <input 
                        type="number" 
                        name="educationCredits" 
                        value={inputs.educationCredits} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-child"></i> Child Tax Credit</label>
                      <input 
                        type="number" 
                        name="childTaxCredit" 
                        value={inputs.childTaxCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Projection Tab */}
          {activeTab === "projection" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-project-diagram"></i>
                  Tax Projection
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-seedling"></i> Current Income</label>
                    <input 
                      type="number" 
                      name="grossIncome" 
                      value={inputs.grossIncome} 
                      onChange={handleChange}
                      placeholder="100000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Projection Years</label>
                    <input 
                      type="number" 
                      name="timePeriod" 
                      value={inputs.timePeriod} 
                      onChange={handleChange}
                      placeholder="10"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Annual Income Growth (%)</label>
                    <input 
                      type="number" 
                      name="annualReturn" 
                      value={inputs.annualReturn} 
                      onChange={handleChange}
                      placeholder="5"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Optimization Tab */}
          {activeTab === "optimization" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-magic"></i>
                  Tax Optimization Strategies
                </h3>
                <div className="scenario-list">
                  <div className="scenario-item">
                    <div className="scenario-name">Tax-Loss Harvesting</div>
                    <div className="scenario-outcome">Potential savings: $3,000 deduction</div>
                    <div className="scenario-stats">
                      <span>Offset capital gains with losses</span>
                      <span>Carry forward unused losses</span>
                    </div>
                  </div>
                  <div className="scenario-item">
                    <div className="scenario-name">Retirement Contributions</div>
                    <div className="scenario-outcome">Potential savings: ${(parseFloat(inputs.retirementContributions) * 0.24).toFixed(0)}</div>
                    <div className="scenario-stats">
                      <span>Reduce taxable income</span>
                      <span>Tax-deferred growth</span>
                    </div>
                  </div>
                  <div className="scenario-item">
                    <div className="scenario-name">Qualified Dividends</div>
                    <div className="scenario-outcome">Preferred 15-20% tax rate</div>
                    <div className="scenario-stats">
                      <span>Lower than ordinary income rates</span>
                      <span>Hold periods matter</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Tax Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No tax calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-percentage"></i>
                          {calc.result.effectiveTotalRate.toFixed(1)}% Eff. Rate
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Tax: ${calc.result.finalTax.toFixed(0)}</span>
                        <span><i className="fas fa-chart-line"></i> Marginal: {calc.result.marginalRate.toFixed(1)}%</span>
                        <span><i className="fas fa-home"></i> State: {calc.inputs.state}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "projection") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-pie"></i>
                    Tax Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value">
                          {result.effectiveTotalRate.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Effective Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.marginalRate.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Marginal Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.finalTax.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Total Tax
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.afterTaxIncome.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          After-tax Income
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Gross Income:</span>
                        <span>${result.grossIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Adjusted Gross Income:</span>
                        <span>${result.agi.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Taxable Income:</span>
                        <span>${result.taxableIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-flag-usa"></i> Federal Income Tax:</span>
                        <span>${result.federalTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Investment Taxes:</span>
                        <span>${result.investmentTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-map-marker-alt"></i> State Tax:</span>
                        <span>${result.stateTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shield-alt"></i> Tax Credits:</span>
                        <span>-${result.totalCredits.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Tax Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Tax Breakdown
                      </h3>
                      <Doughnut 
                        data={taxBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Income Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Income Sources
                      </h3>
                      <Bar 
                        data={incomeBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your income details to see tax calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "projection" && projectionData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Tax Projection
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={projectionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${projectionData[projectionData.length - 1]?.income.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-flag-checkered"></i>
                      Final Income
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${projectionData[projectionData.length - 1]?.estimatedTax.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-file-invoice-dollar"></i>
                      Estimated Tax
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {projectionData.length - 1}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calendar"></i>
                      Years
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value positive">
                      +{((projectionData[projectionData.length - 1]?.income / parseFloat(inputs.grossIncome) - 1) * 100).toFixed(0)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Income Growth
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default EffectiveTaxRateCalculator;